//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { PojoMap } from 'pojo-maps';

import {
  IFacebookPageFeedPostConfig,
  IInstagramFeedAutomaticPostConfig,
  IInstagramFeedManualPostConfig,
  IInstagramReelsAutomaticPostConfig,
  IInstagramReelsManualPostConfig,
  IInstagramStoryGroupAutomaticPostConfig,
  IInstagramStoryGroupPostConfig,
  IOrganicPinConfig,
  ITikTokVideoAutomaticPostConfig,
  ITwitterMediaPostConfig,
  IYouTubeAutomaticPostConfig,
  UniversalScheduledPostConfigType,
} from '@apis/universal-posts/config/universalScheduledPostConfigModel';
import { IUpsertDraftPost } from '@apis/universal-posts/draft-posts/universalDraftPostsApiModel';
import { ICreateUpdateScheduledPost } from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiModel';
import {
  TUniversalPostItem,
  UniversalPostType,
} from '@apis/universal-posts/universalPostsApiModel';
import { getStandardPostDetails } from '@apis/universal-posts/universalPostsUtils';
import { getFacebookScheduledPostType } from '@facebook/utils/facebookScheduledPostUtils';
import { videoThumbnailOffsetSecondsToFiniteDuration } from '@instagram-account/redux/post-details/instagramPostDetailsModel';
import { InstagramManualStoryClip } from '@instagram-account/redux/story-group-post-details/instagramStoryGroupPostDetailsModel';
import Cuid from '@models/Cuid';
import DraftId from '@models/DraftId';
import FacebookPageId from '@models/FacebookPageId';
import {
  toIInstagramLocation,
  toInstagramLocation,
} from '@models/IInstagramLocation';
import { IMlsMediaItem } from '@models/IMlsMediaItem';
import { isVideo } from '@models/INetworkItem';
import { InstagramStoryGroupClipCustomizationType } from '@models/IScheduledInstagramStoryGroup';
import {
  mapInstagramTaggedProductWithCuidToShopTag,
  toInstagramTaggedProduct,
  toShopTag,
} from '@models/IShopTag';
import {
  fromInstagramTaggedUser,
  mapInstagramUserWithCuidToTaggedUser,
  toInstagramTaggedUser,
} from '@models/ITaggedUser';
import PostableId from '@models/PostableId';
import { SocialNetwork } from '@models/SocialNetwork';
import { getStoryCustomizationsIndexAndValue } from '@universal-scheduling/components/scheduler/post-details-forms/postDetailsFormUtils';
import { YouTubeVideoIsSafeForKids } from '@universal-scheduling/components/scheduler/post-details-forms/youtube/YouTubePostDetailsConstants';
import { isPostDetailsTabActive } from '@universal-scheduling/components/scheduler/post-details-tabs/postDetailsTabUtils';
import { PostDetailsFormValues } from '@universal-scheduling/contexts/scheduler/postDetailsFormProviderModels';
import { IPostDetailsTabData } from '@universal-scheduling/contexts/scheduler/schedulerSharedStateModels';
import { getFormKey } from '@universal-scheduling/contexts/scheduler/utils/postDetailsFormContextUtils';
import { TSchedulerFormState } from '@universal-scheduling/models/SchedulerPostDetailsModels';
import { isDefined } from '@utils/filterPredicates';
import { OmitStrict } from '@utils/types/OmitStrict';

type RequireFields<T, K extends keyof T> = T extends any
  ? Omit<T, K> & Required<Pick<T, K>>
  : never;

interface BasePostData {
  editPostId?: PostableId;
  editDraftId?: DraftId;
}

type FormState = TSchedulerFormState & BasePostData;

type DraftFormState = TSchedulerFormState & BasePostData;
type ScheduledPostFormState = RequireFields<
  TSchedulerFormState,
  'scheduledTime'
> &
  BasePostData;

export function mapFormToPostState<T extends true>(
  formData: DraftFormState,
  postingEntityId: string,
  mediaItems: IMlsMediaItem[],
  isDraft: T,
): IUpsertDraftPost;

export function mapFormToPostState<T extends false>(
  formData: ScheduledPostFormState,
  postingEntityId: string,
  mediaItems: IMlsMediaItem[],
  isDraft: T,
): ICreateUpdateScheduledPost;

export function mapFormToPostState<T extends boolean>(
  formData: FormState,
  postingEntityId: string,
  mediaItems: IMlsMediaItem[],
  isDraft: T,
): IUpsertDraftPost | ICreateUpdateScheduledPost {
  const { caption, editDraftId, editPostId, postNow, postType, scheduledTime } =
    formData;
  const cuids = mediaItems.map((item) => item.cuid);
  const now = new Date();
  const time = postNow ? now : scheduledTime;

  if (!isDraft && !isDefined(scheduledTime)) {
    throw new Error('ScheduledTime needs to be defined for scheduled posts');
  }

  const basePost = isDraft
    ? {
        id: editDraftId,
        postingEntityId,
        scheduledTime: time,
        config: {
          cuids: cuids,
        },
      }
    : {
        id: editPostId,
        postingEntityId,
        scheduledTime: time ?? new Date(),
        config: {
          cuids: cuids,
        },
      };

  switch (postType) {
    case UniversalPostType.TwitterMediaPost: {
      return {
        ...basePost,
        config: {
          ...basePost.config,
          text: caption ?? '',
          '@type': UniversalScheduledPostConfigType.TwitterMediaPost,
        },
      };
    }

    case UniversalPostType.TikTokVideoAutomaticPost: {
      const {
        disableComment,
        disableDuet,
        disableStitch,
        thumbnailOffsetInMs,
      } = formData;
      return {
        ...basePost,
        config: {
          ...basePost.config,
          caption: caption,
          disableComment: disableComment ?? false,
          disableDuet: disableDuet ?? false,
          disableStitch: disableStitch ?? false,
          thumbnailOffsetInMs: thumbnailOffsetInMs,
          '@type': UniversalScheduledPostConfigType.TikTokVideoAutomaticPost,
        },
      };
    }

    case UniversalPostType.PinterestOrganicPin: {
      const { boardId, boardSection, title, url } = formData;
      const configProps = { boardId, boardSection, url, title };
      return {
        ...basePost,
        config: {
          ...basePost.config,
          ...configProps,
          description: caption,
          '@type': UniversalScheduledPostConfigType.PinterestOrganicPin,
        },
      };
    }

    case UniversalPostType.FacebookPageFeedPost: {
      const { link, postBodyLinkIds } = formData;
      const configProps = { postBodyLinkIds, link };
      const rootItem = mediaItems[0];
      const videoCuid =
        rootItem?.networkItem && isVideo(rootItem.networkItem)
          ? rootItem.cuid
          : undefined;
      const photoCuids = isDefined(videoCuid) ? undefined : cuids;
      const additionalCuids =
        isDefined(videoCuid) && isDraft ? cuids.slice(1) : undefined;

      const { configType } = getFacebookScheduledPostType(
        photoCuids,
        videoCuid,
        link,
      );
      return {
        ...basePost,
        config: {
          ...basePost.config,
          ...configProps,
          defaultCuid: cuids[0],
          pageId: FacebookPageId(postingEntityId),
          photoCuids,
          status: caption,
          videoCuid,
          additionalCuids,
          '@type': configType,
        },
      };
    }

    case UniversalPostType.YouTubeAutomaticPost: {
      const {
        allowEmbeds,
        categoryId,
        isSafeForKids,
        license,
        playlistIds,
        privacy,
        tags,
        title,
      } = formData;
      return {
        ...basePost,
        config: {
          ...basePost.config,
          allowEmbeds,
          categoryId,
          description: caption,
          isSafeForKids: isSafeForKids === YouTubeVideoIsSafeForKids.Yes,
          license,
          playlistIds,
          privacy,
          tags,
          title,
          '@type': UniversalScheduledPostConfigType.YouTubeAutomaticPost,
        },
      };
    }

    case UniversalPostType.InstagramStoryGroupAutomaticPost:
    case UniversalPostType.InstagramStoryGroupPost:
    case UniversalPostType.InstagramReelsManualPost:
    case UniversalPostType.InstagramReelsAutomaticPost:
    case UniversalPostType.InstagramFeedManualPost:
    case UniversalPostType.InstagramFeedAutomaticPost: {
      const { caption, notifyUserId, postType } = formData;
      const [rootItemCuid, ...additionalCuids] = basePost.config.cuids;

      const baseCreateUpdateScheduledPost = {
        ...basePost,
        config: {
          ...basePost.config,
        },
      };

      const baseFeedPost = {
        ...baseCreateUpdateScheduledPost,
        config: {
          ...baseCreateUpdateScheduledPost.config,
          notifyUserId: notifyUserId,
        },
      };

      const baseNonFeedPost = {
        ...baseCreateUpdateScheduledPost,
        config: {
          ...baseCreateUpdateScheduledPost.config,
          notifyUserId: notifyUserId,
        },
      };

      switch (postType) {
        case UniversalPostType.InstagramStoryGroupPost: {
          const { storyClips } = formData;
          return {
            ...baseNonFeedPost,
            config: {
              ...baseNonFeedPost.config,
              storyClips: mapFormToPostStoryClips(storyClips, isDraft),
              '@type': UniversalScheduledPostConfigType.InstagramStoryGroup,
            },
          };
        }
        case UniversalPostType.InstagramStoryGroupAutomaticPost: {
          return {
            ...baseNonFeedPost,
            config: {
              ...baseNonFeedPost.config,
              '@type':
                UniversalScheduledPostConfigType.InstagramStoryGroupAutomaticPost,
            },
          };
        }

        case UniversalPostType.InstagramFeedManualPost: {
          return {
            ...baseFeedPost,
            config: {
              ...baseFeedPost.config,
              cuid: rootItemCuid,
              caption: caption,
              carouselMedia:
                additionalCuids.length > 0
                  ? additionalCuids.map((cuid, index) => ({
                      index,
                      cuid,
                    }))
                  : undefined,
              '@type': UniversalScheduledPostConfigType.InstagramFeedManualPost,
            },
          };
        }

        case UniversalPostType.InstagramFeedAutomaticPost: {
          const {
            coverFileId,
            firstComment,
            location,
            shopTags,
            thumbnailOffsetInSeconds, // applies thumbnail offset to all videos in a feed
            userTags,
          } = formData;
          const configProps = {
            firstComment,
            location: isDefined(location)
              ? toInstagramLocation(location)
              : undefined,
            shopTags: shopTags
              ? shopTags.map(toInstagramTaggedProduct)
              : undefined,
            userTags: userTags
              ? userTags.map(toInstagramTaggedUser)
              : undefined,
            videoMetadata:
              isDefined(thumbnailOffsetInSeconds) || isDefined(coverFileId)
                ? {
                    thumbOffset: videoThumbnailOffsetSecondsToFiniteDuration(
                      thumbnailOffsetInSeconds,
                    ),
                    coverFileId,
                  }
                : undefined,
          };

          return {
            ...baseFeedPost,
            config: {
              ...baseFeedPost.config,
              ...configProps,
              cuid: rootItemCuid,
              caption: caption,
              carouselMedia:
                additionalCuids.length > 0
                  ? additionalCuids.map((cuid, index) => ({
                      index,
                      cuid,
                    }))
                  : undefined,
              '@type':
                UniversalScheduledPostConfigType.InstagramFeedAutomaticPost,
            },
          };
        }

        case UniversalPostType.InstagramReelsAutomaticPost: {
          const {
            caption,
            coverFileId,
            customizations,
            firstComment,
            location,
            shareToFeed,
            shopTags,
            videoThumbnailOffsetSeconds,
          } = formData;
          const configProps = {
            customizations: {
              ...customizations,
              caption,
            },
            shareToFeed,
            firstComment,
            location: isDefined(location)
              ? toInstagramLocation(location)
              : undefined,
            shopTags: shopTags
              ? shopTags.map(toInstagramTaggedProduct)
              : undefined,
            thumbOffset: isDefined(videoThumbnailOffsetSeconds)
              ? videoThumbnailOffsetSecondsToFiniteDuration(
                  videoThumbnailOffsetSeconds,
                )
              : undefined,
            coverFileId,
          };

          return {
            ...baseNonFeedPost,
            config: {
              ...baseNonFeedPost.config,
              ...configProps,
              '@type':
                UniversalScheduledPostConfigType.InstagramReelsAutomaticPost,
            },
          };
        }
        case UniversalPostType.InstagramReelsManualPost: {
          const { caption, customizations, shareToFeed } = formData;

          return {
            ...baseNonFeedPost,
            config: {
              ...baseNonFeedPost.config,
              customizations: {
                ...customizations,
                caption,
              },
              shareToFeed: shareToFeed,
              '@type':
                UniversalScheduledPostConfigType.InstagramReelsManualPost,
            },
          };
        }
      }
    }
  }
}

const mapFormToPostStoryClips = (
  storyClips: InstagramManualStoryClip[] | undefined,
  isDraft: boolean,
) =>
  (storyClips ?? []).map((clip) => {
    const { index, value: swipeUpLink } = getStoryCustomizationsIndexAndValue(
      InstagramStoryGroupClipCustomizationType.SwipeUpUrl,
      clip.customizations,
    );
    const { cuid, customizations: newCustomizations, fullSwipeUpLink } = clip;

    if (isDraft && fullSwipeUpLink && !swipeUpLink && index > -1) {
      newCustomizations.splice(index, 1, {
        customizationType: InstagramStoryGroupClipCustomizationType.SwipeUpUrl,
        value: fullSwipeUpLink,
      });
    }

    return {
      cuid: cuid,
      customizations: newCustomizations,
    };
  });

export const mapFormToDraftPostState = (
  formData: DraftFormState,
  postingEntityId: string,
  mediaItems: IMlsMediaItem[],
): IUpsertDraftPost => {
  return mapFormToPostState(formData, postingEntityId, mediaItems, true);
};

export const mapFormToScheduledPostState = (
  formData: ScheduledPostFormState,
  postingEntityId: string,
  mediaItems: IMlsMediaItem[],
): ICreateUpdateScheduledPost => {
  return mapFormToPostState(formData, postingEntityId, mediaItems, false);
};

export const mapPostToFormState = (
  post: TUniversalPostItem,
): TSchedulerFormState => {
  const { caption } = getStandardPostDetails(post);

  const base = {
    caption,
    postNow: false,
    scheduledTime: post.scheduledTime,
  };

  switch (post.postType) {
    case UniversalPostType.FacebookPageFeedPost: {
      const config: OmitStrict<
        IFacebookPageFeedPostConfig,
        | 'defaultCuid'
        | 'videoCuid'
        | 'photoCuids'
        | 'pageId'
        | 'status'
        | '@type'
        | 'cuids'
      > = (({
        ['@type']: _,
        cuids,
        defaultCuid,
        pageId,
        photoCuids,
        status,
        videoCuid,
        ...config
      }) => config)(post.config);

      return {
        ...base,
        ...config,
        postType: UniversalPostType.FacebookPageFeedPost,
      };
    }
    case UniversalPostType.TikTokVideoAutomaticPost: {
      const config: OmitStrict<
        ITikTokVideoAutomaticPostConfig,
        '@type' | 'cuids'
      > = (({ ['@type']: _, cuids, ...config }) => config)(post.config);

      return {
        ...base,
        ...config,
        postType: UniversalPostType.TikTokVideoAutomaticPost,
      };
    }
    case UniversalPostType.PinterestOrganicPin: {
      const config: OmitStrict<
        IOrganicPinConfig,
        'description' | '@type' | 'cuids'
      > = (({ ['@type']: _, cuids, description, ...config }) => config)(
        post.config,
      );
      return {
        ...base,
        ...config,
        postType: UniversalPostType.PinterestOrganicPin,
      };
    }
    case UniversalPostType.TwitterMediaPost: {
      const config: Omit<ITwitterMediaPostConfig, 'text' | '@type' | 'cuids'> =
        (({ ['@type']: _, cuids, text, ...config }) => config)(post.config);
      return {
        ...base,
        ...config,
        postType: UniversalPostType.TwitterMediaPost,
      };
    }
    case UniversalPostType.InstagramFeedAutomaticPost: {
      const config: Omit<
        IInstagramFeedAutomaticPostConfig,
        'carouselMedia' | 'cuid' | 'videoMetadata' | '@type' | 'cuids'
      > = (({
        ['@type']: _,
        carouselMedia,
        cuid,
        cuids,
        videoMetadata,
        ...config
      }) => config)(post.config);

      return {
        ...config,
        ...base,
        // TODO: PD-261578: Cleanup curalateUserId to use notifyUserId once the migration is done
        notifyUserId: config.notifyUserId ?? config.curalateUserId,
        location: config.location
          ? toIInstagramLocation(config.location)
          : undefined,
        postType: UniversalPostType.InstagramFeedAutomaticPost,
        shopTags: config.shopTags?.map((tag) => {
          const cuids = post.config.cuids;
          const cuid = cuids[tag.mediaIndex];
          if (isDefined(cuid)) {
            return mapInstagramTaggedProductWithCuidToShopTag(cuid, tag);
          }
          return toShopTag(tag);
        }),
        thumbnailOffsetInSeconds: post.config.videoMetadata?.thumbOffset
          ? post.config.videoMetadata.thumbOffset.length / 1000
          : undefined,
        userTags: config.userTags?.map((tag) => {
          const cuids = post.config.cuids;
          const cuid = cuids[tag.mediaIndex];
          if (isDefined(cuid)) {
            return mapInstagramUserWithCuidToTaggedUser(cuid, tag);
          }
          return fromInstagramTaggedUser(tag);
        }),
        coverFileId: post.config.videoMetadata?.coverFileId,
      };
    }
    case UniversalPostType.InstagramFeedManualPost: {
      const config: Omit<
        IInstagramFeedManualPostConfig,
        'carouselMedia' | 'cuid' | '@type' | 'cuids'
      > = (({ ['@type']: _, carouselMedia, cuid, cuids, ...config }) => config)(
        post.config,
      );

      return {
        ...config,
        ...base,
        // TODO: PD-261578: Cleanup curalateUserId to use notifyUserId once the migration is done
        notifyUserId: config.notifyUserId ?? config.curalateUserId,
        postType: UniversalPostType.InstagramFeedManualPost,
      };
    }
    case UniversalPostType.InstagramReelsAutomaticPost: {
      const config: Omit<
        IInstagramReelsAutomaticPostConfig,
        'thumbOffset' | '@type' | 'cuids'
      > = (({ ['@type']: _, cuids, thumbOffset, ...config }) => config)(
        post.config,
      );

      const { customizations, ...remainingConfig } = config;

      const { caption, ...remainingCustomizations } = customizations ?? {
        caption: undefined,
      };

      return {
        ...remainingConfig,
        ...base,
        customizations: remainingCustomizations,
        caption,
        postType: UniversalPostType.InstagramReelsAutomaticPost,
        location: config.location
          ? toIInstagramLocation(config.location)
          : undefined,
        shopTags: config.shopTags?.map((tag) => {
          const cuids = post.config.cuids;
          const cuid = cuids[tag.mediaIndex];
          if (isDefined(cuid)) {
            return mapInstagramTaggedProductWithCuidToShopTag(cuid, tag);
          }
          return toShopTag(tag);
        }),
        videoThumbnailOffsetSeconds: post.config.thumbOffset
          ? post.config.thumbOffset.length / 1000
          : undefined,
      };
    }
    case UniversalPostType.InstagramReelsManualPost: {
      const config: Omit<IInstagramReelsManualPostConfig, '@type' | 'cuids'> =
        (({ ['@type']: _, cuids, ...config }) => config)(post.config);

      const { customizations, ...remainingConfig } = config;

      const { caption, ...remainingCustomizations } = customizations ?? {
        caption: undefined,
      };

      return {
        ...remainingConfig,
        ...base,
        customizations: remainingCustomizations,
        caption,
        postType: UniversalPostType.InstagramReelsManualPost,
      };
    }
    case UniversalPostType.InstagramStoryGroupAutomaticPost: {
      const config: Omit<
        IInstagramStoryGroupAutomaticPostConfig,
        '@type' | 'cuids'
      > = (({ ['@type']: _, cuids, ...config }) => config)(post.config);
      return {
        ...base,
        ...config,
        postType: UniversalPostType.InstagramStoryGroupAutomaticPost,
      };
    }
    case UniversalPostType.InstagramStoryGroupPost: {
      const config: Omit<IInstagramStoryGroupPostConfig, '@type' | 'cuids'> =
        (({ ['@type']: _, cuids, ...config }) => config)(post.config);
      return {
        ...base,
        ...config,
        storyClips: post.config.storyClips.map((clip) => ({
          cuid: clip.cuid ? Cuid(clip.cuid) : undefined,
          customizations: clip.customizations.map((customization) => ({
            customizationType:
              customization.customizationType as InstagramStoryGroupClipCustomizationType,
            value: customization.value,
          })),
        })),
        postType: UniversalPostType.InstagramStoryGroupPost,
      };
    }
    case UniversalPostType.YouTubeAutomaticPost: {
      const config: OmitStrict<
        IYouTubeAutomaticPostConfig,
        '@type' | 'cuids' | 'isSafeForKids'
      > = (({ ['@type']: _, cuids, isSafeForKids, ...config }) => config)(
        post.config,
      );
      return {
        ...base,
        ...config,
        isSafeForKids: post.config.isSafeForKids
          ? YouTubeVideoIsSafeForKids.Yes
          : YouTubeVideoIsSafeForKids.No,
        postType: UniversalPostType.YouTubeAutomaticPost,
      };
    }
  }
};

export const buildDraftPostData = (
  postDetailsTabData: IPostDetailsTabData[],
  postingEntityIdsMap: PojoMap<SocialNetwork, string>,
  mediaItems: IMlsMediaItem[],
  formData: PostDetailsFormValues,
): IUpsertDraftPost[] => {
  const postData: IUpsertDraftPost[] = postDetailsTabData
    .map((tabData) => {
      const { editDraftId, isActive, socialNetwork } = tabData;
      const postingEntityId = PojoMap.get(postingEntityIdsMap, socialNetwork);

      if (!isDefined(postingEntityId)) {
        console.error(`Could not find posting entity id for ${socialNetwork} `);
      }

      const formKey = getFormKey(tabData.formId, socialNetwork);
      const formValues = formData[formKey];

      if (isActive && formValues && postingEntityId) {
        return mapFormToDraftPostState(
          { ...formValues, editDraftId },
          postingEntityId,
          mediaItems,
        );
      }
    })
    .filter(isDefined);
  return postData;
};

export const buildScheduledPostData = (
  postDetailsTabData: IPostDetailsTabData[],
  postingEntityIdsMap: PojoMap<SocialNetwork, string>,
  mediaItems: IMlsMediaItem[],
  formData: PostDetailsFormValues,
): ICreateUpdateScheduledPost[] => {
  const activePostDetailsTabData = postDetailsTabData.filter(
    isPostDetailsTabActive,
  );

  const postData: ICreateUpdateScheduledPost[] = activePostDetailsTabData
    .map((tabData) => {
      const { editPostId, isActive, socialNetwork } = tabData;
      const postingEntityId = PojoMap.get(postingEntityIdsMap, socialNetwork);

      if (!isDefined(postingEntityId)) {
        console.error(`Could not find posting entity id for ${socialNetwork} `);
      }

      const formKey = getFormKey(tabData.formId, socialNetwork);
      const formValues = formData[formKey];

      const scheduledTime = formValues?.postNow
        ? new Date()
        : formValues?.scheduledTime;

      if (isActive && formValues && postingEntityId) {
        if (isDefined(scheduledTime)) {
          return mapFormToScheduledPostState(
            { ...formValues, scheduledTime, editPostId },
            postingEntityId,
            mediaItems,
          );
        } else {
          console.error(
            `${socialNetwork} form did not have a post time set and was not set to Post Now`,
          );
        }
      }
    })
    .filter(isDefined);

  return postData;
};
